import React, { useEffect, useState } from "react";
import { TrendingUp, MessageCircle, X } from "lucide-react";
import logo from "../../../../../assets/images/icons/logo.svg";
import { Medora360Chat } from "./GenioChat";

interface AISidebarProps {
  patientName: string;
  patientId: number;
}

export const AISidebar: React.FC<AISidebarProps> = ({
  patientName,
  patientId,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [glowPhase, setGlowPhase] = useState(0); // 0: no glow, 1: glowing, 2: fading
  const [isChatOpen, setIsChatOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    // Start glow sequence after 1 second
    const glowTimer = setTimeout(() => {
      setGlowPhase(1);
      // Fade out glow after 1.5 seconds
      setTimeout(() => {
        setGlowPhase(2);
      }, 1500);
    }, 1000);

    return () => {
      clearTimeout(timer);
      clearTimeout(glowTimer);
    };
  }, []);

  const getGlowClass = () => {
    switch (glowPhase) {
      case 1:
        return "shadow-[0_0_25px_rgba(40,35,245,0.6)]";
      case 2:
        return "shadow-[0_0_25px_rgba(40,35,245,0.6)] animate-glow-fade";
      default:
        return "";
    }
  };

  const handleClose = () => {
    const sidebar = document.getElementById("ai-sidebar");
    const fabButton = document.getElementById("ai-fab-button");
    if (sidebar) {
      sidebar.classList.remove("translate-x-0");
      sidebar.classList.add("translate-x-full");
      if (fabButton) {
        fabButton.classList.remove("hidden");
      }
    }
  };

  return (
    <>
      <div
        id="ai-sidebar"
        className="w-full lg:w-80 bg-white dark:bg-darkCardBackground border-l flex flex-col fixed right-0 top-0 h-screen transform lg:translate-x-0 translate-x-full transition-transform duration-500 ease-in-out z-50"
      >
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img src={logo} alt="Medora360 AI" className="w-8 h-8" />
              <h3 className="text-base font-medium text-gray-900 dark:text-white">
                Medora360 AI Insights
              </h3>
            </div>
            <button
              onClick={handleClose}
              className="lg:hidden p-1.5 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md"
            >
              <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>
        <div className="flex-1 px-4 py-3 space-y-4 overflow-y-auto">
          {/* Critical Alerts */}
          <div
            className={`rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-3 transition-all duration-500 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"} hover:border-red-300 dark:hover:border-red-700 hover:shadow-[0_0_15px_rgba(239,68,68,0.1)]`}
          >
            <div className="pb-2">
              <h4 className="text-sm text-red-800 dark:text-red-300 font-medium">
                Critical Alerts
              </h4>
            </div>
            <div className="space-y-2">
              <div className="text-sm text-red-700 dark:text-red-300">
                <div className="font-medium">WCC Elevation</div>
                <div className="text-xs">Monitor for signs of infection</div>
              </div>
            </div>
          </div>

          {/* Key Problems Identified */}
          <div
            className={`rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-3 transition-all duration-500 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"} hover:border-primary/20 dark:hover:border-primary-300/40 hover:shadow-[0_0_15px_rgba(40,35,245,0.1)]`}
            style={{ transitionDelay: "100ms" }}
          >
            <div className="pb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                Key Problems Identified
              </h4>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2.5 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800 hover:border-primary/20 dark:hover:border-primary-300/40 hover:shadow-[0_0_10px_rgba(40,35,245,0.05)] transition-all duration-300">
                <div>
                  <div className="font-medium text-sm text-gray-900 dark:text-white">
                    Post-operative pain management
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Current pain scores indicate need for optimization
                  </div>
                </div>
                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                  Medium
                </span>
              </div>
              <div className="flex items-center justify-between p-2.5 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800 hover:border-primary/20 dark:hover:border-primary-300/40 hover:shadow-[0_0_10px_rgba(40,35,245,0.05)] transition-all duration-300">
                <div>
                  <div className="font-medium text-sm text-gray-900 dark:text-white">
                    Risk of post-op complications
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Elevated WCC requires monitoring
                  </div>
                </div>
                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                  Low
                </span>
              </div>
            </div>
          </div>

          {/* AI-Suggested Next Steps */}
          <div
            className={`rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-3 transition-all duration-500 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"} hover:border-primary/20 dark:hover:border-primary-300/40 hover:shadow-[0_0_15px_rgba(40,35,245,0.1)]`}
            style={{ transitionDelay: "200ms" }}
          >
            <div className="pb-2">
              <h4 className="text-sm flex items-center font-medium text-gray-900 dark:text-white">
                <TrendingUp className="w-4 h-4 mr-2 text-primary dark:text-primary-300" />
                AI-Suggested Next Steps
              </h4>
            </div>
            <div className="space-y-2">
              <div className="flex items-center space-x-3 p-2.5 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-primary/20 dark:hover:border-primary-300/40 hover:shadow-[0_0_10px_rgba(40,35,245,0.05)] transition-all duration-300">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <div className="flex-1">
                  <div className="font-medium text-sm text-gray-900 dark:text-white">
                    Pain score review
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Consider breakthrough analgesia
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-2.5 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-primary/20 dark:hover:border-primary-300/40 hover:shadow-[0_0_10px_rgba(40,35,245,0.05)] transition-all duration-300">
                <div className="w-2 h-2 bg-primary dark:bg-primary rounded-full"></div>
                <div className="flex-1">
                  <div className="font-medium text-sm text-gray-900 dark:text-white">
                    Diet advancement
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Progress to light solids
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className={`p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-darkCardBackground transition-all duration-500 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}
          style={{ transitionDelay: "400ms" }}
        >
          <button
            onClick={() => setIsChatOpen(true)}
            className={`w-full inline-flex items-center justify-center rounded-md text-md font-medium h-12 px-4 py-2 text-white relative overflow-hidden group transition-all duration-500 ${getGlowClass()}`}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary-400 via-primary-300 via-primary-400 to-primary animate-gradient-shift" />
            <MessageCircle className="w-5 h-5 mr-2 relative z-10" />
            <span className="relative z-10 font-bold">Ask Medora360 AI</span>
          </button>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
            Get personalized insights and recommendations
          </p>
        </div>
      </div>

      {/* Medora360Chat Component */}
      <Medora360Chat
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        patientName={patientName}
        patientId={patientId}
      />
    </>
  );
};
